import { useEffect, useState, type ReactElement } from 'react';
import { Box, Group, SimpleGrid, Text } from '@mantine/core';
import { Activity, Coins, ShieldCheck, Users, type LucideIcon } from 'lucide-react';
import { SurfaceCard } from '@/design/SurfaceCard';
import { accentTokens } from '@/design/VizAccents';
import type { VizAccent } from '@/design/VizAccents';
import AIExplorerComposePrompt from './AIExplorerComposePrompt';

interface StarterTheme {
    key: string;
    label: string;
    icon: LucideIcon;
    accent: VizAccent;
    prompts: string[];
}

/** One starter card per surface; each cycles through a handful of prompts. */
const STARTER_THEMES: StarterTheme[] = [
    {
        key: 'adoption',
        label: 'Adoption',
        icon: Users,
        accent: 'teal',
        prompts: [
            'Which teams have embraced AI — and who has gone dark?',
            'Show AI adoption across departments this quarter',
            'Where is adoption growing fastest, and where has it stalled?',
        ],
    },
    {
        key: 'behavior',
        label: 'Behavior',
        icon: Activity,
        accent: 'indigo',
        prompts: [
            'What is AI actually being used for?',
            'Break down usage by task type and difficulty',
            'How has AI usage trended over the last few months?',
        ],
    },
    {
        key: 'token-usage',
        label: 'Token Usage',
        icon: Coins,
        accent: 'amber',
        prompts: ['Where is our token spend going?', 'Which departments drive the most token cost?', "Compare this month's token spend to last month"],
    },
    {
        key: 'governance',
        label: 'Governance',
        icon: ShieldCheck,
        accent: 'violet',
        prompts: [
            'Governance overview: which models are in play and how usage rolls up',
            'Which models are used for the hardest work?',
            'How much AI use is work vs personal across teams?',
        ],
    },
];

const CYCLE_MS = 6000;

interface AIExplorerStarterProps {
    onCompose: (prompt: string) => void;
    composing: boolean;
}

/**
 * The dynamic dashboard's empty state: a compose input plus a set of starter
 * prompt cards — one per surface (adoption / usage / spend / governance) — that
 * cycle through a handful of suggestions. Clicking a card composes its currently
 * shown prompt.
 */
export default function AIExplorerStarter({ onCompose, composing }: AIExplorerStarterProps): ReactElement {
    const [tick, setTick] = useState(0);
    useEffect(() => {
        const advance = (): void => setTick((value) => value + 1);
        const id = setInterval(advance, CYCLE_MS);
        return (): void => clearInterval(id);
    }, []);

    return (
        <Box>
            <AIExplorerComposePrompt onCompose={onCompose} composing={composing} />
            <Text size="xs" fw={700} tt="uppercase" c="dimmed" mt="lg" mb="sm" style={{ letterSpacing: '0.08em' }}>
                Or start from a suggestion
            </Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                {STARTER_THEMES.map((theme) => {
                    const prompt = theme.prompts[tick % theme.prompts.length] ?? theme.prompts[0] ?? '';
                    const tokens = accentTokens(theme.accent);
                    const Icon = theme.icon;
                    const send = (): void => {
                        if (!composing && prompt.length > 0) onCompose(prompt);
                    };
                    return (
                        <SurfaceCard
                            key={theme.key}
                            $padding="md"
                            role="button"
                            tabIndex={0}
                            aria-label={`${theme.label}: ${prompt}`}
                            onClick={send}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    send();
                                }
                            }}
                            style={{ cursor: 'pointer', height: '100%' }}
                        >
                            <Group gap={8} mb="xs" wrap="nowrap" align="center">
                                <Box
                                    style={{
                                        width: 28,
                                        height: 28,
                                        flexShrink: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: tokens.soft,
                                        border: `1px solid ${tokens.border}`,
                                        borderRadius: 'var(--bm-radius)',
                                    }}
                                >
                                    <Icon size={15} color={tokens.solid} />
                                </Box>
                                <Text tt="uppercase" fw={700} style={{ fontSize: '10px', letterSpacing: '0.08em', color: tokens.text }}>
                                    {theme.label}
                                </Text>
                            </Group>
                            <Text fw={600} style={{ fontSize: '14px', lineHeight: 1.35 }}>
                                {prompt}
                            </Text>
                        </SurfaceCard>
                    );
                })}
            </SimpleGrid>
        </Box>
    );
}
